import React, { useState, useEffect, useCallback } from 'react';
import { format, addDays, addWeeks, isSameDay } from 'date-fns';
import api from '../services/api';
import './AppointmentScheduler.css';

function AppointmentScheduler() {
  // Start from today, not beginning of week
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  // availableSlots removed - was unused
  const [patientType, setPatientType] = useState(null); // 'new' or 'existing'
  const [verificationData, setVerificationData] = useState({
    phoneNumber: '',
    dateOfBirth: ''
  });
  const [patientInfo, setPatientInfo] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    dateOfBirth: '',
    insuranceProvider: '',
    insurancePolicyNumber: '',
    insuranceGroupNumber: '',
    reason: ''
  });
  const [verifiedPatient, setVerifiedPatient] = useState(null);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [providersError, setProvidersError] = useState('');
  const [step, setStep] = useState('patientType'); // patientType, verifyPatient, selectProvider, selectDate, selectTime, patientInfo, confirm
  const [continueClicked, setContinueClicked] = useState(false);

  const timeSlots = [
    '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '01:00 PM', '01:30 PM', '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM',
    '04:00 PM', '04:30 PM'
  ];

  // Fetch providers on mount
  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchAvailableSlots = useCallback(async (date) => {
    setLoading(true);
    try {
      const dateStr = date.toISOString().split('T')[0];
      await api.get(`/scheduler/availability`, {
        params: {
          provider_id: selectedProvider?.id,
          date_range: `${dateStr},${dateStr}`
        }
      });
      // Availability check successful
    } catch (error) {
      console.error('Failed to fetch available slots:', error);
    } finally{
      setLoading(false);
    }
  }, [selectedProvider]);

  useEffect(() => {
    if (selectedDate && selectedProvider) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate, selectedProvider, fetchAvailableSlots]);

  // Debug: Log step changes
  useEffect(() => {
    console.log('Step changed to:', step);
  }, [step]);

  // Handle continue button click via effect to bypass React batching issues
  useEffect(() => {
    if (continueClicked) {
      console.log('🔄 useEffect triggered: continueClicked is true');
      if (selectedProvider && step === 'selectProvider') {
        console.log('🔄 Advancing to selectDate');
        setContinueClicked(false); // Reset FIRST to prevent loops
        setStep('selectDate');
      } else {
        console.log('⚠️ Cannot advance - provider or step mismatch');
        setContinueClicked(false);
      }
    }
  }, [continueClicked, selectedProvider, step]);

  const fetchProviders = async () => {
    try {
      setProvidersError('');
      const response = await api.get('/providers');
      if (response.data.success) {
        setProviders(response.data.data);
        console.log('Providers loaded:', response.data.data.length);
      }
    } catch (error) {
      console.error('Failed to fetch providers:', error);
      setProvidersError('Unable to load providers. Please refresh the page.');
      // Set fallback providers for demo
      setProviders([
        { id: 1, fullName: 'John Smith', specialty: 'Family Medicine', title: 'Dr.' },
        { id: 2, fullName: 'Sarah Johnson', specialty: 'Internal Medicine', title: 'Dr.' }
      ]);
    }
  };

  // Removed duplicate fetchAvailableSlots definition - now using useCallback above

  const getWeekDays = () => {
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time for comparison

    // Show next 5 business days starting from today or current selected week
    // ⚠️ CRITICAL FIX: Create NEW date object to avoid mutating state
    let startDate = new Date(currentWeek);
    startDate.setHours(0, 0, 0, 0);

    // Ensure we don't show past dates
    if (startDate < today) {
      startDate = new Date(today);
    }

    // Generate up to 14 days to ensure we get 5 business days (safety limit)
    let daysChecked = 0;
    let currentDay = new Date(startDate);

    while (days.length < 5 && daysChecked < 14) {
      const dayOfWeek = currentDay.getDay();
      // Skip weekends (0 = Sunday, 6 = Saturday)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        days.push(new Date(currentDay));
      }
      currentDay = addDays(currentDay, 1);
      daysChecked++;
    }

    return days.slice(0, 5); // Return exactly 5 days
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setStep('selectTime');
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setStep('patientInfo');
  };

  const handlePatientTypeSelect = (type) => {
    console.log('Patient type selected:', type);
    setPatientType(type);
    if (type === 'existing') {
      setStep('verifyPatient');
    } else {
      setStep('selectProvider');
    }
  };

  // Removed unused handleProviderSelect function

  const handleVerifyInputChange = (e) => {
    setVerificationData({
      ...verificationData,
      [e.target.name]: e.target.value
    });
  };

  const handleVerifyPatient = async (e) => {
    e.preventDefault();
    setLoading(true);
    setVerifyError('');

    try {
      const response = await api.post('/patients/verify', {
        phoneNumber: verificationData.phoneNumber,
        dateOfBirth: verificationData.dateOfBirth
      });

      if (response.data.success) {
        setVerifiedPatient(response.data.data);
        // Pre-fill patient info
        setPatientInfo({
          ...patientInfo,
          firstName: response.data.data.firstName || response.data.data.fname,
          lastName: response.data.data.lastName || response.data.data.lname,
          phoneNumber: response.data.data.phoneNumber || response.data.data.phoneCell || response.data.data.phone_cell,
          email: response.data.data.email || '',
          dateOfBirth: verificationData.dateOfBirth
        });
        setStep('selectProvider');
      }
    } catch (error) {
      console.error('Verification failed:', error);
      const errorMsg = error.response?.data?.error || 'Patient not found. Please verify the information or register as a new patient.';
      setVerifyError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setPatientInfo({
      ...patientInfo,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStep('confirm');
  };

  const confirmAppointment = async () => {
    setLoading(true);
    try {
      const appointmentData = {
        ...patientInfo,
        patientId: verifiedPatient?.patient_id || verifiedPatient?.pid,
        providerId: selectedProvider?.id,
        date: selectedDate.toISOString(),
        time: selectedTime,
        status: 'scheduled'
      };

      const response = await api.post('/appointments', appointmentData);

      if (response.data.success) {
        let message = '✅ Appointment scheduled successfully!\n\nPatient: ' + patientInfo.firstName + ' ' + patientInfo.lastName + '\nDate: ' + selectedDate.toLocaleDateString() + '\nTime: ' + selectedTime;

        // Show warning if in fallback mode
        if (response.data.warning) {
          message += '\n\n⚠️ Note: ' + response.data.warning;
        }

        alert(message);
        resetForm();
      }
    } catch (error) {
      console.error('Appointment error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error occurred';
      alert('❌ Failed to schedule appointment.\n\nError: ' + errorMessage + '\n\nPlease check your information and try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setPatientType(null);
    setVerifiedPatient(null);
    setVerificationData({ phoneNumber: '', dateOfBirth: '' });
    setSelectedProvider(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setPatientInfo({
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
      dateOfBirth: '',
      insuranceProvider: '',
      insurancePolicyNumber: '',
      insuranceGroupNumber: '',
      reason: ''
    });
    setVerifyError('');
    setStep('patientType');
  };

  const goBack = () => {
    if (step === 'verifyPatient') {
      setStep('patientType');
    } else if (step === 'selectProvider') {
      if (patientType === 'existing') {
        setStep('verifyPatient');
      } else {
        setStep('patientType');
      }
    } else if (step === 'selectDate') {
      setStep('selectProvider');
    } else if (step === 'selectTime') {
      setStep('selectDate');
      setSelectedTime(null);
    } else if (step === 'patientInfo') {
      setStep('selectTime');
    } else if (step === 'confirm') {
      setStep('patientInfo');
    }
  };

  return (
    <div className="appointment-scheduler">
      <div className="scheduler-header">
        <h2>Schedule an Appointment</h2>
        {step !== 'patientType' && (
          <button className="back-btn" onClick={goBack}>
            ← Back
          </button>
        )}
      </div>

      {/* Debug: Show current step */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          padding: '8px',
          backgroundColor: '#f0f0f0',
          fontSize: '12px',
          borderRadius: '4px',
          marginBottom: '10px'
        }}>
          Debug - Current Step: <strong>{step}</strong> | Provider: {selectedProvider?.fullName || 'None'}
        </div>
      )}

      {!['patientType', 'verifyPatient', 'selectProvider'].includes(step) && (
        <div className="progress-bar">
          <div className={`progress-step ${step === 'selectDate' ? 'active' : ['selectTime', 'patientInfo', 'confirm'].includes(step) ? 'completed' : ''}`}>
            <div className="step-number">1</div>
            <div className="step-label">Select Date</div>
          </div>
          <div className={`progress-step ${step === 'selectTime' ? 'active' : ['patientInfo', 'confirm'].includes(step) ? 'completed' : ''}`}>
            <div className="step-number">2</div>
            <div className="step-label">Select Time</div>
          </div>
          <div className={`progress-step ${step === 'patientInfo' ? 'active' : step === 'confirm' ? 'completed' : ''}`}>
            <div className="step-number">3</div>
            <div className="step-label">Patient Info</div>
          </div>
          <div className={`progress-step ${step === 'confirm' ? 'active' : ''}`}>
            <div className="step-number">4</div>
            <div className="step-label">Confirm</div>
          </div>
        </div>
      )}

      {/* Step 0: Patient Type Selection */}
      {step === 'patientType' && (
        <div className="patient-type-selection">
          <h3>Are you a new patient or an existing patient?</h3>
          <div className="type-buttons">
            <button
              className="type-btn new-patient"
              onClick={() => handlePatientTypeSelect('new')}
            >
              <div className="btn-icon">👤</div>
              <div className="btn-title">New Patient</div>
              <div className="btn-description">First time visiting our office</div>
            </button>
            <button
              className="type-btn existing-patient"
              onClick={() => handlePatientTypeSelect('existing')}
            >
              <div className="btn-icon">✓</div>
              <div className="btn-title">Existing Patient</div>
              <div className="btn-description">I've been here before</div>
            </button>
          </div>
        </div>
      )}

      {/* Step 0b: Verify Existing Patient */}
      {step === 'verifyPatient' && (
        <div className="verify-patient">
          <h3>Verify Your Identity</h3>
          <p>Please enter your information to verify your existing patient record.</p>
          <form onSubmit={handleVerifyPatient}>
            <div className="form-group">
              <label>Phone Number *</label>
              <input
                type="tel"
                name="phoneNumber"
                value={verificationData.phoneNumber}
                onChange={handleVerifyInputChange}
                placeholder="(555) 123-4567"
                required
              />
            </div>
            <div className="form-group">
              <label>Date of Birth *</label>
              <input
                type="date"
                name="dateOfBirth"
                value={verificationData.dateOfBirth}
                onChange={handleVerifyInputChange}
                required
              />
            </div>
            {verifyError && (
              <div className="error-message">
                {verifyError}
              </div>
            )}
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify Identity'}
            </button>
          </form>
        </div>
      )}

      {/* Step: Select Provider */}
      {step === 'selectProvider' && (
        <div className="provider-selection">
          <h3>Select Your Provider</h3>
          <p>Choose the doctor you would like to see for this appointment.</p>
          {providersError && (
            <div className="error-message" style={{ marginBottom: '1rem' }}>
              {providersError}
            </div>
          )}
          {providers.length === 0 ? (
            <div className="loading">Loading providers...</div>
          ) : (
            <>
              <div className="provider-list">
                {providers.map((provider) => (
                  <button
                    key={provider.id}
                    className={`provider-card ${selectedProvider?.id === provider.id ? 'selected' : ''}`}
                    onClick={() => setSelectedProvider(provider)}
                    type="button"
                  >
                    <div className="provider-info">
                      <div className="provider-name">
                        {provider.title ? `${provider.title} ` : ''}
                        {provider.firstName} {provider.lastName}
                      </div>
                      {provider.specialty && (
                        <div className="provider-specialty">{provider.specialty}</div>
                      )}
                    </div>
                    <div className="provider-select-icon">
                      {selectedProvider?.id === provider.id ? '✓' : '→'}
                    </div>
                  </button>
                ))}
              </div>
              {selectedProvider && (
                <div style={{ marginTop: '1.5rem' }}>
                  <div style={{
                    fontSize: '12px',
                    color: '#666',
                    marginBottom: '8px',
                    padding: '8px',
                    backgroundColor: '#e8f5e9',
                    borderRadius: '4px'
                  }}>
                    ✓ Provider selected: {selectedProvider.fullName}
                  </div>
                  <button
                    className="submit-btn"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('🔘 Continue button clicked');
                      console.log('📍 Current step:', step);
                      console.log('👤 Selected provider:', selectedProvider);
                      console.log('🎯 Triggering continueClicked state');
                      setContinueClicked(true);
                    }}
                    style={{
                      width: '100%',
                      position: 'relative',
                      zIndex: 9999,
                      cursor: 'pointer'
                    }}
                  >
                    Continue to Select Date →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Step 1: Select Date */}
      {step === 'selectDate' && (
        <div className="date-selection">
          <h3>Select a Date</h3>
          <p>Choose a date for your appointment with {selectedProvider?.fullName}</p>
          <div className="week-navigator">
            <button
              onClick={() => {
                const newWeek = addWeeks(currentWeek, -1);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                // Don't go back past today
                if (newWeek >= today) {
                  setCurrentWeek(newWeek);
                }
              }}
              disabled={currentWeek <= new Date()}
            >
              ‹ Previous
            </button>
            <span>{format(currentWeek, 'MMMM yyyy')}</span>
            <button onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}>
              Next ›
            </button>
          </div>
          <div className="week-days">
            {getWeekDays().map((day, index) => (
              <button
                key={index}
                className={`day-card ${selectedDate && isSameDay(day, selectedDate) ? 'selected' : ''}`}
                onClick={() => handleDateSelect(day)}
              >
                <div className="day-name">{format(day, 'EEE')}</div>
                <div className="day-number">{format(day, 'd')}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Select Time */}
      {step === 'selectTime' && (
        <div className="time-selection">
          <h3>Select a time for {format(selectedDate, 'EEEE, MMMM d, yyyy')}</h3>
          {loading ? (
            <div className="loading">Loading available times...</div>
          ) : (
            <div className="time-slots">
              {timeSlots.map((time, index) => (
                <button
                  key={index}
                  className={`time-slot ${selectedTime === time ? 'selected' : ''}`}
                  onClick={() => handleTimeSelect(time)}
                >
                  {time}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 3: Patient Information */}
      {step === 'patientInfo' && (
        <div className="patient-info-form">
          <h3>{patientType === 'existing' ? 'Confirm Your Information' : 'Enter Your Information'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={patientInfo.firstName}
                  onChange={handleInputChange}
                  readOnly={patientType === 'existing'}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={patientInfo.lastName}
                  onChange={handleInputChange}
                  readOnly={patientType === 'existing'}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Phone Number *</label>
              <input
                type="tel"
                name="phoneNumber"
                value={patientInfo.phoneNumber}
                onChange={handleInputChange}
                placeholder="(555) 123-4567"
                readOnly={patientType === 'existing'}
                required
              />
            </div>
            {/* Show DOB field for new patients only (existing already verified) */}
            {patientType === 'new' && (
              <div className="form-group">
                <label>Date of Birth *</label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={patientInfo.dateOfBirth}
                  onChange={handleInputChange}
                  required
                />
              </div>
            )}
            <div className="form-group">
              <label>Email (optional)</label>
              <input
                type="email"
                name="email"
                value={patientInfo.email}
                onChange={handleInputChange}
                placeholder="patient@example.com"
              />
            </div>

            {/* Insurance Information - New patients only */}
            {patientType === 'new' && (
              <>
                <div className="insurance-section">
                  <h4>Insurance Information</h4>
                  <p className="section-note">Please provide your insurance details if you have coverage.</p>
                </div>
                <div className="form-group">
                  <label>Insurance Provider *</label>
                  <input
                    type="text"
                    name="insuranceProvider"
                    value={patientInfo.insuranceProvider}
                    onChange={handleInputChange}
                    placeholder="e.g., Blue Cross, Aetna, UnitedHealthcare"
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Policy Number *</label>
                    <input
                      type="text"
                      name="insurancePolicyNumber"
                      value={patientInfo.insurancePolicyNumber}
                      onChange={handleInputChange}
                      placeholder="Policy/Member ID"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Group Number (optional)</label>
                    <input
                      type="text"
                      name="insuranceGroupNumber"
                      value={patientInfo.insuranceGroupNumber}
                      onChange={handleInputChange}
                      placeholder="Group ID"
                    />
                  </div>
                </div>
              </>
            )}
            <div className="form-group">
              <label>Reason for Visit *</label>
              <textarea
                name="reason"
                value={patientInfo.reason}
                onChange={handleInputChange}
                rows="3"
                placeholder="Brief description of your symptoms or reason for visit"
                required
              />
            </div>
            <button type="submit" className="submit-btn">
              Continue to Confirmation
            </button>
          </form>
        </div>
      )}

      {/* Step 4: Confirmation */}
      {step === 'confirm' && (
        <div className="confirmation">
          <h3>Confirm Your Appointment</h3>
          <div className="appointment-summary">
            <div className="summary-section">
              <h4>Appointment Details</h4>
              <div className="detail-row">
                <span className="label">Provider:</span>
                <span className="value">
                  {selectedProvider?.title ? `${selectedProvider.title} ` : ''}
                  {selectedProvider?.fullName}
                  {selectedProvider?.specialty && ` - ${selectedProvider.specialty}`}
                </span>
              </div>
              <div className="detail-row">
                <span className="label">Date:</span>
                <span className="value">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</span>
              </div>
              <div className="detail-row">
                <span className="label">Time:</span>
                <span className="value">{selectedTime}</span>
              </div>
              <div className="detail-row">
                <span className="label">Reason:</span>
                <span className="value">{patientInfo.reason}</span>
              </div>
            </div>
            <div className="summary-section">
              <h4>Patient Information</h4>
              <div className="detail-row">
                <span className="label">Name:</span>
                <span className="value">{patientInfo.firstName} {patientInfo.lastName}</span>
              </div>
              <div className="detail-row">
                <span className="label">Phone:</span>
                <span className="value">{patientInfo.phoneNumber}</span>
              </div>
              <div className="detail-row">
                <span className="label">Email:</span>
                <span className="value">{patientInfo.email || 'Not provided'}</span>
              </div>
            </div>
            {patientType === 'new' && patientInfo.insuranceProvider && (
              <div className="summary-section">
                <h4>Insurance Information</h4>
                <div className="detail-row">
                  <span className="label">Provider:</span>
                  <span className="value">{patientInfo.insuranceProvider}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Policy Number:</span>
                  <span className="value">{patientInfo.insurancePolicyNumber}</span>
                </div>
                {patientInfo.insuranceGroupNumber && (
                  <div className="detail-row">
                    <span className="label">Group Number:</span>
                    <span className="value">{patientInfo.insuranceGroupNumber}</span>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="confirm-actions">
            <button
              className="confirm-btn"
              onClick={confirmAppointment}
              disabled={loading}
            >
              {loading ? 'Scheduling...' : 'Confirm Appointment'}
            </button>
            <button className="cancel-btn" onClick={resetForm}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AppointmentScheduler;
